import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { MessageSquare, CheckCircle, Clock, Zap, Pause, Play, Trash2, ExternalLink, Users, TrendingUp, ChevronDown, ChevronUp, Star, BarChart3, Calendar } from 'lucide-react';

export default function MyAITeam() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadAgents(); }, []);
  const loadAgents = async () => { try { const r = await api.get('/api/agents/team-details'); setAgents(r.data || []); } catch(e){ console.error(e); } finally { setLoading(false); } };

  const handleStatusChange = async (agentId, newStatus) => {
    try { await api.patch(`/api/agents/${agentId}/status`, { status: newStatus }); loadAgents(); } catch(e){ console.error(e); }
  };

  const formatTime = (ts) => { if (!ts) return 'N/A'; const d = new Date(ts); const diff = Math.floor((Date.now() - d) / 60000); if (diff < 1) return 'Just now'; if (diff < 60) return `${diff}m ago`; if (diff < 1440) return `${Math.floor(diff/60)}h ago`; return d.toLocaleDateString(); };

  const totalTasks = agents.reduce((s, a) => s + (a.metrics?.tasks_completed || 0), 0);
  const totalMessages = agents.reduce((s, a) => s + (a.total_messages || 0), 0);
  const totalHours = agents.reduce((s, a) => s + (a.metrics?.hours_saved || 0), 0);

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in">
      {/* Hero Banner */}
      <div style={{ background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)', borderRadius: 20, padding: '32px 36px', marginBottom: 24, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>My AI Team</h2>
          <p style={{ opacity: 0.85, fontSize: 14 }}>Your autonomous HR workforce — deploy, manage, and monitor performance.</p>
        </div>
        <div style={{ display: 'flex', gap: 28 }}>
          {[{ val: agents.length, label: 'Team Size', icon: Users }, { val: totalTasks, label: 'Tasks Done', icon: CheckCircle }, { val: totalMessages, label: 'Messages', icon: MessageSquare }, { val: totalHours.toFixed(1), label: 'Hrs Saved', icon: Clock }].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <s.icon size={18} style={{ marginBottom: 4, opacity: 0.8 }} />
              <div style={{ fontSize: 26, fontWeight: 800 }}>{s.val}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {agents.length === 0 ? (
        <div className="empty-state" style={{ padding: 60 }}>
          <Users size={72} />
          <h4 style={{ fontSize: 22 }}>No AI Employees Deployed</h4>
          <p>Deploy AI employees to build your autonomous HR team.</p>
          <button className="btn-action" onClick={() => navigate('/deploy')} style={{ padding: '14px 28px', fontSize: 15, borderRadius: 12, marginTop: 8 }}>
            <Zap size={18} style={{ marginRight: 8 }} />Deploy Your First AI Employee
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 20 }}>
          {agents.map(agent => {
            const isExpanded = expandedAgent === agent.id;
            return (
              <div key={agent.id} style={{ background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden', transition: 'all 0.3s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'}
                data-testid={`team-agent-${agent.agent_type}`}>
                {/* Color bar */}
                <div style={{ height: 4, background: `linear-gradient(90deg, ${agent.color}, ${agent.color}88)` }} />
                
                {/* Agent Card */}
                <div style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    <div onClick={() => navigate(`/workspace/${agent.id}`)} style={{ cursor: 'pointer', borderRadius: 16, overflow: 'hidden', boxShadow: `0 4px 12px ${agent.color}30`, border: `2px solid ${agent.color}20`, flexShrink: 0 }}>
                      <AgentAvatar agentType={agent.agent_type} size={72} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{agent.agent_name}</h3>
                        <span className={`agent-status ${agent.status}`} style={{ fontSize: 11 }}>
                          <span className={`status-dot ${agent.status}`} />{agent.status}
                        </span>
                      </div>
                      <p style={{ fontSize: 13, color: agent.color, fontWeight: 600, marginBottom: 4 }}>{agent.agent_title}</p>
                      <p style={{ fontSize: 12, color: '#94a3b8' }}>Active {agent.days_active} days &middot; Deployed {new Date(agent.deployed_at).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                    {[
                      { val: agent.metrics?.tasks_completed || 0, label: 'Tasks', color: '#2E7D32' },
                      { val: agent.total_messages || 0, label: 'Msgs', color: '#7B1FA2' },
                      { val: (agent.metrics?.hours_saved || 0).toFixed(1), label: 'Hrs', color: '#1565C0' },
                      { val: `${agent.metrics?.accuracy_score || 95}%`, label: 'Accuracy', color: '#F9A825' },
                    ].map(m => (
                      <div key={m.label} style={{ background: `${m.color}08`, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: m.color }}>{m.val}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{m.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Specialties */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {(agent.specialties || []).slice(0, 4).map(s => (
                      <span key={s} style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{s}</span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-action" style={{ flex: 1, borderRadius: 10, padding: '10px', fontSize: 13 }} onClick={() => navigate(`/workspace/${agent.id}`)} data-testid={`open-workspace-${agent.agent_type}`}>
                      <ExternalLink size={14} style={{ marginRight: 6 }} />Open Workspace
                    </button>
                    {agent.status === 'active' ? (
                      <button className="btn-secondary" style={{ borderRadius: 10, padding: '10px 14px' }} onClick={() => handleStatusChange(agent.id, 'paused')} title="Pause"><Pause size={16} /></button>
                    ) : (
                      <button style={{ borderRadius: 10, padding: '10px 14px', background: '#22c55e', color: 'white', border: 'none', cursor: 'pointer' }} onClick={() => handleStatusChange(agent.id, 'active')} title="Resume"><Play size={16} /></button>
                    )}
                    <button onClick={() => setExpandedAgent(isExpanded ? null : agent.id)} className="btn-secondary" style={{ borderRadius: 10, padding: '10px 14px' }}>
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{ padding: '0 24px 20px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ paddingTop: 16 }}>
                      <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 16 }}>{agent.description}</p>
                      
                      {/* Recent Tasks */}
                      {(agent.recent_tasks || []).length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                          <h5 style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Recent Tasks</h5>
                          {agent.recent_tasks.map(t => (
                            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                              {t.status === 'completed' ? <CheckCircle size={12} color="#22c55e" /> : <Clock size={12} color="#f59e0b" />}
                              <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{t.task_name}</span>
                              <span style={{ fontSize: 10, color: '#cbd5e1' }}>{formatTime(t.created_at)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Available Tasks */}
                      <div style={{ marginBottom: 14 }}>
                        <h5 style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>Available Tasks ({(agent.task_templates || []).length})</h5>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(agent.task_templates || []).map(t => (
                            <span key={t.id} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, background: `${agent.color}10`, color: agent.color, fontWeight: 600 }}>{t.name}</span>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ fontSize: 12, color: '#64748b' }}>Consultations: <strong>{agent.consultations_made || 0}</strong> made, <strong>{agent.consulted_by_others || 0}</strong> received</div>
                      </div>

                      <button className="btn-delete" onClick={() => { if (window.confirm(`Remove ${agent.agent_name}?`)) handleStatusChange(agent.id, 'removed'); }} style={{ width: '100%', marginTop: 12, borderRadius: 10, fontSize: 12 }}>
                        <Trash2 size={13} style={{ marginRight: 4 }} />Remove from Team
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
